import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Phone, Scroll, ShieldCheck, TreeDeciduous, Users } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAction, useApi } from '../lib/useApi';
import { greetingEnglish, greetingKey, useFamilyTerms } from '../lib/terms';
import { FamilyLanguages } from '../components/FamilyLanguages';
import { FamilyProgress } from '../components/FamilyProgress';
import { FirstSteps } from '../components/FirstSteps';
import { ErrorBox, Loading, Notice, PageHeader, StatusBadge, plural, PasswordInput, LoadFailed } from '../components/ui';

export interface Child {
  id: string;
  display_name: string;
  age_band: string;
  track: 'kids' | 'teen';
  avatar_key: string;
  active: boolean;
  cowries: number;
  has_teen_login: boolean;
}
interface FamilyResp {
  family: { name: string };
  members: Array<{ id: string; display_name: string; role: string; relationship_label: string | null; status: string }>;
  children: Child[];
  maxChildren: number;
}

export const AVATAR: Record<string, string> = { cowrie: '🐚', drum: '🥁', baobab: '🌳', sun: '☀️', river: '🌊', lion: '🦁', tortoise: '🐢', star: '⭐' };


export function DashboardPage() {
  const { user, enterChildMode } = useAuth();
  const { data, error, loading, reload } = useApi<FamilyResp>('/family');
  const nav = useNavigate();
  const act = useAction();
  const [invite, setInvite] = useState({ email: '', displayName: '', role: 'grandparent', relationshipLabel: '' });
  const [devLink, setDevLink] = useState<string | null>(null);
  // Greets by the hour, in the family's own language when a native speaker has
  // approved the words — otherwise plainly in English.
  const { term, primary } = useFamilyTerms();

  const cowries = data?.children.reduce((n, c) => n + (c.cowries ?? 0), 0) ?? 0;

  if (loading) return <Loading />;
  if (error || !data) return <LoadFailed error={error} onRetry={reload} />;
  const isParent = user?.role === 'parent';

  return (
    <>
      <section className="hero-band">
        <div className="hero-copy">
          <span className="kicker" lang={term(greetingKey()) ? primary?.code : 'en'}>
            {term(greetingKey()) ?? greetingEnglish()}
          </span>
          <h1 className="display yo">{data.family.name}</h1>
          <p className="lead">Everything here is private to your family. No public profiles, no messages between adults and children.</p>
        </div>
        <div className="hero-stats" aria-label="This family at a glance">
          <div>
            <b className="stat">{data.children.length}</b>
            <span className="muted small">{plural(data.children.length, 'child', 'children')}</span>
          </div>
          <div>
            <b className="stat">{cowries}</b>
            <span className="muted small">{plural(cowries, 'cowrie')} earned</span>
          </div>
          <div>
            <b className="stat">{data.members.length}</b>
            <span className="muted small">family {plural(data.members.length, 'member')}</span>
          </div>
        </div>
      </section>
      {isParent && <FirstSteps childCount={data.children.length} members={data.members.length} />}
      <FamilyProgress />
      <FamilyLanguages canEdit={isParent} />
      <section className="grid grid-3" aria-label="Children">
        {data.children.map((c) => (
          <div className="card stack" key={c.id}>
            <div className="spread">
              <h2 style={{ margin: 0 }}>
                <span aria-hidden="true">{AVATAR[c.avatar_key] ?? '🐚'}</span> <span className="yo">{c.display_name}</span>
              </h2>
              <span className="badge">{c.track === 'teen' ? 'Roots · teen' : 'Kids'} · {c.age_band}</span>
            </div>
            <p>
              <span className="cowrie">🐚 {c.cowries}</span> <span className="muted small">cowries earned</span>
            </p>
            {!c.active && <StatusBadge status="revoked" />}
            {isParent && c.active && (
              <div className="row">
                {c.track === 'kids' && (
                  <button
                    className="btn small"
                    onClick={async () => {
                      const r = await act.run(() => api<{ token: string }>(`/family/children/${c.id}/child-session`, { method: 'POST', body: {} }));
                      if (r) {
                        enterChildMode(r.token, c.id, c.display_name);
                        nav('/play');
                      }
                    }}
                  >
                    Play as {c.display_name}
                  </button>
                )}
                {c.track === 'teen' && !c.has_teen_login && <TeenLoginButton childId={c.id} onDone={reload} />}
                {c.track === 'teen' && c.has_teen_login && <span className="badge ok">has own sign-in</span>}
              </div>
            )}
          </div>
        ))}
        {isParent && data.children.length < data.maxChildren && (
          <div className="card stack">
            <h2>Add a child</h2>
            <p className="muted">Up to {data.maxChildren} children per family plan. You’ll give verified parental consent first.</p>
            <Link className="btn secondary" to="/consent">
              Start consent
            </Link>
          </div>
        )}
      </section>

      <section className="grid grid-2" style={{ marginTop: 20 }}>
        <div className="card">
          <h2>Family members</h2>
          <ul>
            {data.members.map((m) => (
              <li key={m.id}>
                <span className="yo">{m.display_name}</span> — {m.relationship_label ?? m.role} {m.status !== 'active' && <StatusBadge status={m.status} />}
              </li>
            ))}
          </ul>
          {isParent && (
            <form
              className="stack"
              onSubmit={async (e) => {
                e.preventDefault();
                const r = await act.run(() =>
                  api<{ emailed: boolean; warning?: string; inviteLink?: string }>('/family/invites', {
                    body: { ...invite, relationshipLabel: invite.relationshipLabel || undefined },
                  }),
                );
                if (r) {
                  // Never say "sent" unless it was. A grandmother who was never
                  // written to looks exactly like one who ignored the message.
                  act.setNotice(r.emailed ? `Invitation sent to ${invite.email}.` : (r.warning ?? 'Invitation created, but no email was sent.'));
                  setDevLink(r.inviteLink ?? null);
                  if (r.emailed) setInvite({ email: '', displayName: '', role: 'grandparent', relationshipLabel: '' });
                  reload();
                }
              }}
            >
              <h3>Invite a grandparent or co-parent</h3>
              <div className="row">
                <input aria-label="Name" placeholder="Name" value={invite.displayName} onChange={(e) => setInvite({ ...invite, displayName: e.target.value })} required className="yo" />
                <input aria-label="Email" placeholder="Email" type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} required />
                <select aria-label="Role" value={invite.role} onChange={(e) => setInvite({ ...invite, role: e.target.value })}>
                  <option value="grandparent">Grandparent</option>
                  <option value="parent">Parent</option>
                </select>
                <input aria-label="Relationship" placeholder="e.g. Ìyá àgbà" value={invite.relationshipLabel} onChange={(e) => setInvite({ ...invite, relationshipLabel: e.target.value })} className="yo" />
                <button className="btn small" disabled={act.busy}>
                  Invite
                </button>
              </div>
              {devLink && (
                <p className="small">
                  Their link — send it to them yourself:{' '}
                  <button type="button" className="linky" onClick={() => void navigator.clipboard?.writeText(devLink)}>
                    copy
                  </button>{' '}
                  <span className="muted" style={{ wordBreak: 'break-all' }}>{devLink}</span>
                </p>
              )}
            </form>
          )}
          <Notice text={act.notice} />
          <ErrorBox error={act.error} />
        </div>
      </section>

      <section className="grid grid-2" style={{ marginTop: 20 }}>
        <div className="card stack">
          <h2>Quick links</h2>
          <div className="row">
            <Link className="btn secondary small" to="/tree"><TreeDeciduous size={16} strokeWidth={1.75} aria-hidden="true" /> Voice Tree</Link>
            <Link className="btn secondary small" to="/stories"><Scroll size={16} strokeWidth={1.75} aria-hidden="true" /> Record a story</Link>
            <Link className="btn secondary small" to="/live"><Users size={16} strokeWidth={1.75} aria-hidden="true" /> Live Circles</Link>
            <Link className="btn secondary small" to="/calls"><Phone size={16} strokeWidth={1.75} aria-hidden="true" /> Call a grandparent</Link>
          </div>
          <div className="alert safety small with-icon">
            <ShieldCheck size={18} strokeWidth={1.75} aria-hidden="true" />
            <span>Ìran has no private messaging and no public profiles. Tutors only ever meet children in recorded group circles that parents can join.</span>
          </div>
        </div>
      </section>
    </>
  );
}

function TeenLoginButton({ childId, onDone }: { childId: string; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const act = useAction();
  if (!open)
    return (
      <button className="btn small secondary" onClick={() => setOpen(true)}>
        Give own sign-in
      </button>
    );
  return (
    <form
      className="stack"
      onSubmit={async (e) => {
        e.preventDefault();
        if (await act.run(() => api(`/family/children/${childId}/teen-login`, { body: { email, password } }))) onDone();
      }}
    >
      <input aria-label="Teen email" type="email" placeholder="Teen's email" value={email} onChange={(e) => setEmail(e.target.value)} required />
      <PasswordInput ariaLabel="Teen password" placeholder="Password (10+ chars)" minLength={10} value={password} onChange={setPassword} autoComplete="new-password" required />
      <p className="small muted">The teen’s account stays linked to your family and your privacy settings.</p>
      <ErrorBox error={act.error} />
      <button className="btn small" disabled={act.busy}>
        Create teen sign-in
      </button>
    </form>
  );
}
